import DownloadIcon from '@mui/icons-material/FileDownloadOutlined';
import EmergencyOverrideDetailPanel from './EmergencyOverrideDetailPanel';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PropTypes from 'prop-types';
import React, {useCallback, useMemo, useState} from 'react';
import WarningAmberIcon from '@mui/icons-material/WarningAmberOutlined';
import routePath, {Routes} from '@account/utils/route';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import {DataGridPro} from '@mui/x-data-grid-pro';
import {DateTime} from 'luxon';
import {Link as RouterLink} from 'react-router-dom';
import {getEmergencyOverridesExport} from '@account/api/admin-reports';

const TYPE_LABELS = {
  product_addition: 'Product Addition',
  quota_adjustment: 'Quota Adjustment',
};

const TYPE_COLORS = {
  product_addition: 'primary',
  quota_adjustment: 'warning',
};

const EmergencyOverridesTable = ({
  data,
  count,
  isLoading,
  error,
  start,
  end,
  page,
  pageSize,
  onPageChange,
}) => {
  const columns = useMemo(
    () => [
      {
        field: 'created_at',
        headerName: 'Date',
        width: 160,
        valueFormatter: params =>
          params.value
            ? DateTime.fromISO(params.value).toLocaleString(
                DateTime.DATETIME_MED
              )
            : '—',
      },
      {
        field: 'type',
        headerName: 'Type',
        width: 160,
        renderCell: params =>
          params.value ? (
            <Chip
              label={TYPE_LABELS[params.value] ?? params.value}
              color={TYPE_COLORS[params.value] ?? 'default'}
              size="small"
              variant="outlined"
            />
          ) : (
            '—'
          ),
      },
      {
        field: 'ca_name',
        headerName: 'Customer Account',
        flex: 1,
        minWidth: 180,
        // Falls back to org_name while backend migrates to ca_name/ca_id
        valueGetter: params => params.row.ca_name ?? params.row.org_name,
        renderCell: params => {
          const id = params.row.ca_id ?? params.row.org_id;
          const name = params.row.ca_name ?? params.row.org_name;
          if (!id || !name) return '—';
          return (
            <Link
              component={RouterLink}
              to={routePath(Routes.ACCOUNT_DETAIL, {accountId: id})}
              variant="body2"
              underline="hover"
              onClick={e => e.stopPropagation()}
            >
              {name}
            </Link>
          );
        },
      },
      {
        field: 'product_name',
        headerName: 'Product',
        width: 160,
        valueFormatter: params => params.value ?? '—',
      },
      {
        field: 'jira_ticket',
        headerName: 'JIRA',
        width: 120,
      },
      {
        field: 'reason',
        headerName: 'Reason',
        flex: 2,
        minWidth: 200,
      },
      {
        field: 'created_by',
        headerName: 'Created By',
        width: 180,
        valueFormatter: params => params.value ?? '—',
      },
    ],
    []
  );

  const [expandedRowIds, setExpandedRowIds] = useState([]);

  const getDetailPanelContent = useCallback(
    ({row}) => <EmergencyOverrideDetailPanel row={row} />,
    []
  );

  const getDetailPanelHeight = useCallback(() => 'auto', []);

  // DataGridPro requires a stable, unique id per row. Prefer the row's own id;
  // fall back to a composite of stable fields when the API omits one.
  const getRowId = useCallback(
    row =>
      row.id ??
      [
        row.created_at,
        row.type,
        row.ca_id ?? row.org_id,
        row.product_name,
        row.jira_ticket,
      ].join('|'),
    []
  );

  const [exportError, setExportError] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    setExportError(null);
    setIsExporting(true);
    try {
      const blob = await getEmergencyOverridesExport({start, end});
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `emergency-overrides-${start}-${end}.csv`;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      setExportError(err?.message || 'CSV export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          mb={1}
        >
          <Stack direction="row" alignItems="center" gap={1}>
            <WarningAmberIcon color="warning" fontSize="small" />
            <Typography variant="h6">Emergency Overrides</Typography>
            {count !== null && count !== undefined && (
              <Chip
                label={count}
                size="small"
                color={count > 0 ? 'warning' : 'default'}
                sx={{fontWeight: 700}}
              />
            )}
          </Stack>
          <Button
            size="small"
            startIcon={
              isExporting ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <DownloadIcon />
              )
            }
            onClick={handleExport}
            disabled={!count || isExporting}
          >
            Export CSV
          </Button>
        </Stack>

        <Typography variant="body2" color="text.secondary" mb={2}>
          Manual provisioning actions that bypassed the normal self-service or
          SFDC workflow. Includes both <strong>product additions</strong>{' '}
          (adding a new entitlement to a CA) and{' '}
          <strong>quota adjustments</strong> (modifying an existing
          entitlement). Each requires a JIRA ticket and an explicit
          confirmation.
        </Typography>

        {error && (
          <Alert severity="error" sx={{mb: 2}}>
            Failed to load emergency overrides.
          </Alert>
        )}

        {exportError && (
          <Alert severity="error" sx={{mb: 2}}>
            {exportError}
          </Alert>
        )}

        {!error && count === 0 && !isLoading && (
          <Typography variant="body2" color="text.secondary">
            No emergency overrides in this time period.
          </Typography>
        )}

        {(isLoading || (data && data.length > 0)) && (
          <Box sx={{height: 320}}>
            <DataGridPro
              rows={data ?? []}
              columns={columns}
              getRowId={getRowId}
              loading={isLoading}
              disableRowSelectionOnClick
              paginationMode="server"
              rowCount={count ?? 0}
              paginationModel={{page, pageSize}}
              onPaginationModelChange={model => onPageChange(model.page)}
              pageSizeOptions={[pageSize]}
              density="compact"
              getDetailPanelContent={getDetailPanelContent}
              getDetailPanelHeight={getDetailPanelHeight}
              detailPanelExpandedRowIds={expandedRowIds}
              onDetailPanelExpandedRowIdsChange={setExpandedRowIds}
              slots={{
                detailPanelExpandIcon: KeyboardArrowDownIcon,
                detailPanelCollapseIcon: KeyboardArrowUpIcon,
              }}
              sx={{
                '& .MuiDataGrid-cell': {fontSize: 13},
                '& .MuiDataGrid-row:hover': {cursor: 'pointer'},
              }}
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

EmergencyOverridesTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  count: PropTypes.number,
  isLoading: PropTypes.bool,
  error: PropTypes.object,
  start: PropTypes.string.isRequired,
  end: PropTypes.string.isRequired,
  page: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
};

export default EmergencyOverridesTable;
